
import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

const steps = [
  {
    title: "1. Describe Your Vision",
    description: "Start with a simple prompt. Describe the app or website you want to build."
  },
  {
    title: "2. Codey Builds Files And Code",
    description: "Codey analyzes your request, makes detailed plans and writes high-quality code."
  },
  {
    title: "3. Instant Preview And Deploynment",
    description: "See your creation live instantly, Use 'Codey Edit' to refine and perfect your application. Deploy"
  }
];

const useTypewriter = (text: string, speed = 50, start = false, isHtml = false) => {
    const [displayText, setDisplayText] = useState('');
    
    useEffect(() => {
        if (!start) return;
        setDisplayText(''); // Reset on step change
        let i = 0;
        const typingInterval = setInterval(() => {
            if (i < text.length) {
                setDisplayText(prevText => prevText + text.charAt(i));
                i++;
            } else {
                clearInterval(typingInterval);
            }
        }, speed);

        return () => clearInterval(typingInterval);
    }, [text, speed, start]);

    if (isHtml) {
        return <span dangerouslySetInnerHTML={{ __html: displayText }} />;
    }
    return <>{displayText}</>;
};

const StepItem = memo(({ title, description, isActive, onClick }: { title: string; description: string; isActive: boolean; onClick: () => void; }) => (
    <button onClick={onClick} className={`p-6 rounded-lg text-left transition-all duration-300 w-full ${isActive ? 'bg-white/10 scale-105' : 'hover:bg-white/5'}`}>
        <h3 className={`font-bold text-xl ${isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>{title}</h3>
        <p className={`text-sm mt-1 ${isActive ? 'text-[var(--text-secondary)]' : 'text-gray-500'}`}>{description}</p>
    </button>
));

const HowItWorks: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const sectionRef = useRef<HTMLDivElement>(null);
    const isVisible = useIntersectionObserver(sectionRef, { threshold: 0.3 });
    const intervalRef = useRef<number | null>(null);

    const codeText = `<span class="tag-color">function</span> <span class="attr-color">TodoList</span>() {
  <span class="tag-color">return</span> (
    <span class="tag-color">&lt;div</span> <span class="attr-color">className</span>=<span class="string-color">"todo-card"</span><span class="tag-color">&gt;</span>
      <span class="tag-color">&lt;h2&gt;</span>My Project Tasks<span class="tag-color">&lt;/h2&gt;</span>
      <span class="tag-color">&lt;ul&gt;</span>
        <span class="tag-color">&lt;li&gt;</span>Design the landing page<span class="tag-color">&lt;/li&gt;</span>
        <span class="tag-color">&lt;li&gt;</span>Integrate user authentication<span class="tag-color">&lt;/li&gt;</span>
        <span class="tag-color">&lt;li&gt;</span>Deploy to production<span class="tag-color">&lt;/li&gt;</span>
      <span class="tag-color">&lt;/ul&gt;</span>
    <span class="tag-color">&lt;/div&gt;</span>
  );
}`;

    const typedCode = useTypewriter(codeText, 20, isVisible && activeStep === 1, true);

    const startAutoCycle = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(() => {
            setActiveStep(prev => (prev + 1) % steps.length);
        }, 6000);
    }, []);
    
    useEffect(() => {
        if (isVisible) {
            startAutoCycle();
        } else {
            if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isVisible, startAutoCycle]);
    
    const handleStepClick = useCallback((index: number) => {
        setActiveStep(index);
        startAutoCycle();
    }, [startAutoCycle]);

    return (
        <section ref={sectionRef} className={`py-20 sm:py-32 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <style>{`
                /* Step visibility logic */
                .animation-viewport > div { position: absolute; inset: 0; opacity: 0; transition: opacity 0.3s ease-in-out; }
                .how-it-works-interactive.step-1 .anim-step-1 { opacity: 1; }
                .how-it-works-interactive.step-2 .anim-step-2 { opacity: 1; }
                .how-it-works-interactive.step-3 .anim-step-3 { opacity: 1; }

                /* Blinking cursor for typewriter effect */
                .typing-text::after { content: '|'; animation: blink 0.7s infinite; }
                @keyframes blink { 50% { opacity: 0; } }

                /* Step 2: Syntax highlighting */
                .tag-color { color: #e06c75; }
                .attr-color { color: #d19a66; }
                .string-color { color: #98c379; }

                /* Step 3: Staggered animations */
                @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
                @keyframes fadeInDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                
                .step-3 .anim-item { opacity: 0; }
                .step-3 .browser-window { animation: scaleIn 0.4s ease-out forwards; }
                .step-3 .anim-nav { animation: fadeInDown 0.5s ease-out 0.4s forwards; }
                .step-3 .anim-hero { animation: fadeInDown 0.5s ease-out 0.6s forwards; }
                .step-3 .anim-card:nth-child(1) { animation: fadeInUp 0.5s ease-out 0.8s forwards; }
                .step-3 .anim-card:nth-child(2) { animation: fadeInUp 0.5s ease-out 1.0s forwards; }
                .step-3 .anim-card:nth-child(3) { animation: fadeInUp 0.5s ease-out 1.2s forwards; }
            `}</style>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--text-primary)]">How It Works</h2>
                    <p className="mt-4 max-w-2xl mx-auto text-lg text-[var(--text-secondary)]">
                        Go from idea to live application in three simple steps.
                    </p>
                </div>
                
                <div className={`grid lg:grid-cols-2 gap-12 items-center how-it-works-interactive step-${activeStep + 1}`}>
                    {/* Left Column: Steps */}
                    <div className="space-y-4 steps-list">
                        {steps.map((step, index) => (
                            <StepItem 
                                key={index}
                                title={step.title}
                                description={step.description}
                                isActive={activeStep === index}
                                onClick={() => handleStepClick(index)}
                            />
                        ))}
                    </div>
                    
                    {/* Right Column: Animation */}
                    <div className="glow-card relative bg-black/20 border border-[var(--border-color)] rounded-xl p-4 h-96 animation-viewport">
                        {/* Animation for Step 1 */}
                        <div className="anim-step-1 w-full h-full p-4 bg-[#0d1117] rounded-md font-code text-left text-sm flex flex-col justify-center items-start">
                            <div className="flex items-center space-x-1.5 mb-4">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>
                            <p className="text-gray-400">// Describe your vision...</p>
                            <div className="mt-2 p-4 border border-dashed border-gray-600 rounded-md w-full">
                                <p className="text-white">A modern to-do list app with a dark theme, user authentication, and the ability to categorize tasks.</p>
                            </div>
                            <p className="mt-4 text-gray-400">// Then let Codey handle the rest!</p>
                        </div>
                        {/* Animation for Step 2 */}
                        <div className="anim-step-2 w-full h-full p-4 bg-[#0d1117] rounded-md font-code text-left text-sm overflow-auto">
                            <div className="flex items-center space-x-1.5 mb-4">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>
                            <pre><code className="typing-text">{typedCode}</code></pre>
                        </div>
                        {/* Animation for Step 3 */}
                        <div className="anim-step-3 w-full h-full bg-[#f3f4f6] dark:bg-[#0d1117] rounded-md flex flex-col items-start overflow-hidden border border-[var(--border-color)] browser-window anim-item">
                            {/* Mock Browser Header */}
                            <div className="w-full p-2 bg-gray-200 dark:bg-black/30 flex items-center space-x-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                            </div>
                            {/* Mock Website Content */}
                            <div className="w-full flex-grow p-4 space-y-3 bg-white dark:bg-transparent">
                                <div className="anim-nav anim-item w-full p-2 rounded flex justify-between items-center bg-gray-100 dark:bg-white/5">
                                    <div className="w-1/4 h-4 bg-[var(--gradient-from)] rounded"></div>
                                    <div className="flex space-x-2"><div className="w-12 h-3 bg-gray-400 dark:bg-gray-600 rounded"></div><div className="w-12 h-3 bg-gray-400 dark:bg-gray-600 rounded"></div></div>
                                </div>
                                <div className="anim-hero anim-item w-full p-8 rounded flex flex-col items-center justify-center bg-gray-200 dark:bg-white/10">
                                    <div className="w-3/4 h-5 bg-gray-700 dark:bg-gray-300 rounded mb-3"></div>
                                    <div className="w-1/3 h-6 bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded"></div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="anim-card anim-item p-2 rounded bg-gray-100 dark:bg-white/5"><div className="w-full h-12 bg-gray-300 dark:bg-gray-700 rounded"></div></div>
                                    <div className="anim-card anim-item p-2 rounded bg-gray-100 dark:bg-white/5"><div className="w-full h-12 bg-gray-300 dark:bg-gray-700 rounded"></div></div>
                                    <div className="anim-card anim-item p-2 rounded bg-gray-100 dark:bg-white/5"><div className="w-full h-12 bg-gray-300 dark:bg-gray-700 rounded"></div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;